/**
 * A PDF small enough to build rather than commit.
 *
 * The browser-driven document tests (`e2e-web/document-reader-column.spec.ts`)
 * measure the real full screen reader, and the reader is *about* a document: it
 * draws nothing — no page, no `+`, no card over a page — until pdf.js has parsed
 * one. A repository has no business carrying a binary fixture for that, and a
 * downloaded one would be a licence to audit, so the document is assembled here
 * from the specification's own parts: a catalogue, a page tree, two pages, and a
 * content stream each.
 *
 * Deliberately **no text**: a glyph needs a font, a font needs the standard-font
 * data pdf.js fetches at render time, and a fixture that depends on a fetch to
 * draw is a fixture that fails for a reason that is not the thing under test. A
 * filled rectangle is a page that paints with nothing but the bytes below.
 *
 * The cross-reference table is the part that has to be right — every entry is that
 * object's byte offset in the file — so the offsets are recorded as the file is
 * written rather than counted by hand (a wrong offset is a document pdf.js either
 * refuses or silently repairs, and a repaired parse is not what the reader does
 * with a real file).
 *
 * The pages are portrait 300×400 unless a size is given. That size is a parameter
 * because a page's *shape* is what some of the reader's furniture follows — a
 * landscape document is how the rail's tiles were caught being square
 * (`e2e-web/document-reader-thumbnails.spec.ts`).
 */

/** US Letter's shape, near enough: what matters is that it is portrait. */
const PAGE_WIDTH = 300
const PAGE_HEIGHT = 400

/** The two pages' fills, so a page can be told from its neighbour on screen. */
const PAGE_TINTS = ['0.85 0.85 0.85', '0.72 0.72 0.72']

export function minimalPdf(
  pages = 2,
  size: {width: number; height: number} = {
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
  },
): Uint8Array {
  const chunks: Uint8Array[] = []
  let length = 0
  const encoder = new TextEncoder()

  const write = (text: string) => {
    const bytes = encoder.encode(text)
    chunks.push(bytes)
    length += bytes.length
  }

  /** Where each object starts, for the xref table. */
  const offsets: number[] = []
  const object = (id: number, body: string) => {
    offsets[id] = length
    write(`${id} 0 obj\n${body}\nendobj\n`)
  }

  /*
   * 1: the catalogue, 2: the page tree, then a page and a stream per page —
   * so a page object is `3 + 2i` and its content is one along from it.
   */
  const firstPageObject = 3
  const pageObject = (index: number) => firstPageObject + index * 2
  const contentObject = (index: number) => pageObject(index) + 1
  const count = 2 + pages * 2

  write('%PDF-1.4\n')
  object(1, '<< /Type /Catalog /Pages 2 0 R >>')
  object(
    2,
    `<< /Type /Pages /Count ${pages} /Kids [${Array.from(
      {length: pages},
      (_, index) => `${pageObject(index)} 0 R`,
    ).join(' ')}] >>`,
  )

  for (let index = 0; index < pages; index++) {
    object(
      pageObject(index),
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${size.width} ${size.height}] ` +
        `/Contents ${contentObject(index)} 0 R >>`,
    )
    // A rectangle inset from the paper's edges, in this page's own tint.
    const stream = `${PAGE_TINTS[index % PAGE_TINTS.length]} rg 24 24 ${
      size.width - 48
    } ${size.height - 48} re f`
    object(
      contentObject(index),
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    )
  }

  const xrefStart = length
  let xref = `xref\n0 ${count + 1}\n0000000000 65535 f \n`
  for (let id = 1; id <= count; id++) {
    xref += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  }
  write(xref)
  write(
    `trailer\n<< /Size ${count + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`,
  )

  const pdf = new Uint8Array(length)
  let at = 0
  for (const chunk of chunks) {
    pdf.set(chunk, at)
    at += chunk.length
  }
  return pdf
}
